var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import tinycolor from "tinycolor2";
import * as Inflected from "inflected";
import { useRef, useEffect, useState, useCallback } from "react";
class WindowMessenger {
  constructor(config) {
    __publicField(this, "pendingMessages", /* @__PURE__ */ new Map());
    __publicField(this, "messageIdCounter", 0);
    __publicField(this, "messageHandlers", {});
    __publicField(this, "handleMessage", (event) => {
      try {
        const { id, type, ...data } = event.data || {};
        if (event.origin !== this.config.targetOrigin || !id) return;
        if (this.pendingMessages.has(id)) {
          const { resolve } = this.pendingMessages.get(id);
          this.pendingMessages.delete(id);
          resolve(data);
          return;
        }
        if (type && this.messageHandlers[type]) {
          const result = this.messageHandlers[type](data);
          Promise.resolve(result).then((replyData) => {
            this.config.sendMessage(
              { id, ...replyData || {} },
              this.config.targetOrigin
            );
          });
        } else {
          this.config.sendMessage({ id }, this.config.targetOrigin);
        }
      } catch (error) {
        console.error("Error handling message:", error);
      }
    });
    this.config = config;
    window.addEventListener("message", this.handleMessage);
  }
  cleanup() {
    window.removeEventListener("message", this.handleMessage);
    this.pendingMessages.clear();
    this.messageHandlers = {};
  }
  sendMessage(type, data = {}) {
    return new Promise((resolve, reject) => {
      const id = `msg_${this.messageIdCounter++}_${Date.now()}`;
      this.pendingMessages.set(id, { resolve, reject });
      this.config.sendMessage({ id, type, ...data }, this.config.targetOrigin);
    });
  }
  onMessage(type, handler) {
    this.messageHandlers[type] = handler;
  }
}
const COLORS_WITH_BORDER = ["#ffffff"];
const isDarkModeEnabled = () => document.body.getAttribute("data-theme") === "dark";
const mixColorForTheme = (color, weight = 90, forceDark) => {
  const mixedColor = tinycolor.mix(
    color,
    forceDark || isDarkModeEnabled() ? "#21282e" : "white",
    weight
  );
  return mixedColor.toRgbString();
};
const colorRequiresBorder = (color) => COLORS_WITH_BORDER.some(
  (colorWithBorder) => tinycolor.equals(colorWithBorder, color)
);
const getCssValue = (varName) => {
  return getComputedStyle(document.body).getPropertyValue(varName);
};
const decodeCssVars = (vars) => {
  const cssVarRegex = /(?:var\()?--[\w-]+(?:\))?/g;
  const cssVars = vars.match(cssVarRegex) || [];
  const resolvedVarsMap = /* @__PURE__ */ new Map();
  cssVars.forEach((varName) => {
    if (!resolvedVarsMap.has(varName)) {
      const cleanVarName = varName.replace(/^var\(/, "").replace(/\)$/, "");
      const resolvedValue = getCssValue(cleanVarName);
      resolvedVarsMap.set(varName, resolvedValue);
    }
  });
  let result = vars;
  resolvedVarsMap.forEach((resolvedValue, varName) => {
    if (resolvedValue) {
      const varRegex = new RegExp(
        varName.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"),
        "g"
      );
      result = result.replace(varRegex, resolvedValue);
    }
  });
  return result;
};
const decodeColor = (_color) => {
  let color = _color;
  if (_color.includes("--")) {
    color = decodeCssVars(_color);
  }
  if (color.startsWith("rgba(")) {
    const alpha = 1 - tinycolor(color).getAlpha();
    const hex = tinycolor(color).toHex();
    const mixed = tinycolor.mix(tinycolor(hex), "#ffffff", alpha * 100);
    return mixed.toHexString();
  }
  return tinycolor(color).toHexString();
};
const preventNextKey = ({
  key,
  eventName
}) => {
  const preventNextKeyup = (event) => {
    if (event.key === key) {
      event.preventDefault();
      event.stopPropagation();
      document.removeEventListener(eventName, preventNextKeyup, true);
    }
  };
  document.addEventListener(eventName, preventNextKeyup, true);
};
function pluralize(count, text, locale) {
  return count === 1 ? text : Inflected.pluralize(text, locale);
}
Inflected.inflections("en", (inflect) => {
  inflect.plural(/^sec\.$/i, "secs.");
  inflect.plural(/^min\.$/i, "mins.");
  inflect.plural(/^hr\.$/i, "hrs.");
});
const featureIdMapping = {
  develop: {
    featureId: "7051611331030433659",
    flavorName: "Develop",
    suitePath: "/suite/develop"
  },
  teamwork: {
    featureId: "7497344015132535572",
    flavorName: "Teamwork",
    suitePath: "/suite/teamwork"
  },
  ideas: {
    featureId: "7176377937479403267",
    flavorName: "Ideas",
    suitePath: "/suite/ideas"
  },
  roadmap: {
    featureId: "7099078319733540170",
    flavorName: "Roadmaps",
    suitePath: "/suite/roadmaps"
  },
  academy: {
    featureId: "7153332896823181858",
    flavorName: "Academy",
    suiteUrl: "https://www.aha.io/academy/certifications"
  },
  enterprise_plus: {
    featureId: "7148074887366420668",
    flavorName: "Roadmaps E+",
    suitePath: "/suite/roadmaps/enterprise_plus"
  },
  whiteboards: {
    featureId: "7333230858416702915",
    flavorName: "Whiteboards",
    suitePath: "/suite/whiteboards"
  },
  knowledge: {
    featureId: "7333230212709605344",
    flavorName: "Knowledge",
    suitePath: "/suite/knowledge"
  },
  discovery: {
    featureId: "7475787212180571759",
    flavorName: "Discovery",
    suitePath: "/suite/discovery"
  }
};
function getFlavorDetails(upgradeLocation) {
  const flavorMatch = Object.keys(featureIdMapping).find(
    (flavor) => upgradeLocation == null ? void 0 : upgradeLocation.startsWith(flavor)
  );
  if (flavorMatch) {
    return featureIdMapping[flavorMatch];
  }
}
function handlePromoView(upgradeLocation, eventName) {
  var _a;
  const flavorDetails = getFlavorDetails(upgradeLocation);
  if (flavorDetails && window.crossPromos) {
    const { featureId, flavorName } = flavorDetails;
    (_a = window.AhaAnalytics) == null ? void 0 : _a.track(
      `Feature-${featureId}`,
      eventName || "User views promotion",
      {
        "Upgrade location": window.crossPromos[upgradeLocation],
        "Promotion product": flavorName
      }
    );
  }
}
function handleCtaClick(_event, upgradeLocation, anchor) {
  var _a;
  const flavorDetails = getFlavorDetails(upgradeLocation);
  if (flavorDetails && window.crossPromos) {
    const { featureId, flavorName, suitePath, suiteUrl } = flavorDetails;
    (_a = window.AhaAnalytics) == null ? void 0 : _a.track(
      `Feature-${featureId}`,
      "User clicks promotion",
      {
        "Upgrade location": window.crossPromos[upgradeLocation],
        "Promotion product": flavorName
      }
    );
    if (suiteUrl) {
      window.open(suiteUrl, "_blank", "noopener noreferrer");
    } else {
      const url = `${suitePath}?upgrade_location=${upgradeLocation}${anchor ? `#${anchor}` : ""}`;
      window.open(url, "_blank", "noopener noreferrer");
    }
  }
}
class RequestQueue {
  constructor() {
    __publicField(this, "queue");
    __publicField(this, "working");
    this.queue = [];
    this.working = false;
  }
  // Enqueues a request to the end of the queue
  enqueueRequest(request, followup) {
    this.queue.push([request, followup]);
    if (!this.working) {
      this.working = true;
      this.doWork();
    }
  }
  // Enqueues a request to the start of the queue
  prependRequest(request, followup) {
    this.queue.unshift([request, followup]);
    if (!this.working) {
      this.working = true;
      this.doWork();
    }
  }
  async doWork() {
    const [request, followup] = this.queue.shift();
    await request();
    followup && followup();
    if (this.queue.length > 0) {
      this.doWork();
    } else {
      this.working = false;
    }
  }
}
function inGroupsOf(array, groupSize, fillValue = null) {
  if (groupSize <= 0) {
    throw new Error("groupSize must be greater than 0");
  }
  const result = [];
  for (let i = 0; i < array.length; i += groupSize) {
    const group = array.slice(i, i + groupSize);
    if (fillValue !== false) {
      while (group.length < groupSize) {
        group.push(fillValue);
      }
    }
    result.push(group);
  }
  return result;
}
const useEffectAfterMount = (cb, dependencies = []) => {
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) {
      return cb();
    }
    mounted.current = true;
  }, dependencies);
};
const getStoredValue = (key, sanitise, session) => {
  const storage = session ? sessionStorage : localStorage;
  const raw = storage.getItem(key);
  if (raw === void 0 || raw === null) {
    return sanitise(void 0);
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    console.warn(
      `Invalid JSON in ${session ? "sessionStorage" : "localStorage"} ${key}`,
      key,
      raw
    );
  }
  return sanitise(parsed);
};
const usePersistedState = (key, sanitise, persist = false) => {
  const [value, setValue] = useState(
    () => getStoredValue(key, sanitise, !persist)
  );
  const setValueWrapped = useCallback(
    (val) => {
      const storage = persist ? localStorage : sessionStorage;
      const prevState = getStoredValue(key, sanitise, !persist);
      let updated;
      if (typeof val === "function") {
        updated = val(prevState);
      } else {
        updated = val;
      }
      storage.setItem(key, JSON.stringify(updated));
      setValue(updated);
    },
    [persist, key, sanitise]
  );
  return [value, setValueWrapped];
};
export {
  RequestQueue,
  WindowMessenger,
  colorRequiresBorder,
  decodeColor,
  decodeCssVars,
  featureIdMapping,
  getCssValue,
  getFlavorDetails,
  handleCtaClick,
  handlePromoView,
  inGroupsOf,
  isDarkModeEnabled,
  mixColorForTheme,
  pluralize,
  preventNextKey,
  useEffectAfterMount,
  usePersistedState
};
//# sourceMappingURL=index.js.map
